import { type FC, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import type {
    SideMenuExpandClickHandler,
    SideMenuLinkClickHandler,
} from '../../../common/components/molecules/SideMenu/SideMenu.types';

import { SkipLink, SkipLinkLayout, SkipLinkTarget } from '../../../base/components/SkipLink';
import SideMenu from '../../../common/components/molecules/SideMenu/SideMenu';
import { useProductSectionsContext } from '../../contexts/productSectionsContext';
import { useCurrentSectionParams } from '../../hooks/useCurrentSectionParams';
import useReactRouting from '../../hooks/useReactRouting';
import useSectionMenuState from './SectionMenu.hooks';

interface SectionMenuProps {
    onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, name: string | null) => void;
    onLastLevelLinkClick?: (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        name: string | null,
    ) => void;
}

export const SectionMenu: FC<React.PropsWithChildren<SectionMenuProps>> = ({
    onLinkClick,
    onLastLevelLinkClick,
}) => {
    const { sectionTree, sectionMenu } = useProductSectionsContext();

    const { section1, section2, section3, productId } = useCurrentSectionParams(sectionTree);

    const section1Id = section1?.id;
    const section2Id = section2?.id;
    const section3Id = section3?.id;

    const { state, activateLevel, collapseLevel, expandLevel, collapseAllLevels, activeIdRef } =
        useSectionMenuState();

    useEffect(() => {
        if (!section1Id && !section2Id && !section3Id) {
            collapseAllLevels();
            return;
        }

        if (section1Id && section2Id && !section3Id) {
            collapseLevel(3);
            return;
        }

        if (section1Id && !section2Id) {
            collapseLevel(2);
            return;
        }

        if (!section1Id) {
            collapseLevel(1);
        }
    }, [section1Id, section2Id, section3Id, collapseAllLevels, collapseLevel]);

    useEffect(() => {
        if (section1Id) {
            expandLevel(section1Id, 1);
            activateLevel(section1Id, 1);
        }

        if (section2Id) {
            expandLevel(section2Id, 2);
            activateLevel(section2Id, 2);
        }

        if (section3Id) {
            expandLevel(section3Id, 3);
            activateLevel(section3Id, 3);
        }
    }, [section1Id, section2Id, section3Id, expandLevel, activateLevel]);

    const { push } = useReactRouting();

    const handleLinkClick: SideMenuLinkClickHandler = ({ e, url, id, hasChildren, name }) => {
        e.stopPropagation();
        e.preventDefault();
        if (activeIdRef.current !== id || productId) {
            push(url);
            if (!hasChildren) {
                onLastLevelLinkClick?.(e, name);
            } else {
                onLinkClick?.(e, name);
            }
        }
    };

    const handleExpandClick: SideMenuExpandClickHandler = ({ e, id: sectionId, level }) => {
        e.stopPropagation();
        e.preventDefault();

        if (
            (level === 1 && state.expanded[0] === sectionId) ||
            (level === 2 && state.expanded[1] === sectionId)
        ) {
            collapseLevel(level);
        } else if (level === 1 || level === 2 || level === 3) {
            expandLevel(sectionId, level);
        }
    };

    const { pathname } = useLocation();

    return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
            {!!sectionMenu && (
                <SkipLinkTarget id="ecomSections">
                    <SkipLinkLayout
                        skipTriggerSlot={
                            <SkipLink href="#pageContent" align="center">
                                Hoppa över sortiment
                            </SkipLink>
                        }
                    >
                        <SideMenu
                            currentPath={pathname}
                            label="Sortiment"
                            rootNode={sectionMenu}
                            activeNodesTrail={state.active.filter(Boolean)}
                            expandedNodesTrail={state.expanded.filter(Boolean)}
                            onLinkClick={handleLinkClick}
                            onExpandClick={handleExpandClick}
                        >
                            {/* Added an empty item as a quick fix to prevent the last item from beeing hidden by the chat button or the browser status bar. }} */}
                            <li className="SidebarNav-item u-bgNone has-children">
                                <div className="SidebarNav-heading u-borderBottomNone" />
                            </li>
                        </SideMenu>
                    </SkipLinkLayout>
                </SkipLinkTarget>
            )}
        </>
    );
};

export default SectionMenu;
